import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LanguageService } from 'src/services/language.service';
import { UserService } from 'src/services/user.service';
import { EtablissementService } from 'src/services/etablissement.service';
import { SelectedEtablissementService } from 'src/services/selected-etablissement.service';
import { ChatNotificationService } from 'src/services/chat-notification';
import { InscritService } from 'src/services/inscrit.service';
import { WebSocketService } from 'src/services/WebSocketService';
import { AuthenticationService } from 'src/services/authentication.service';
import { Role } from 'src/model/model.role';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() isLoginPage: boolean = false;
  @Input() isSidebarOpen: boolean = false;
  @Output() sidebarToggled = new EventEmitter<boolean>();
  basePath = environment.BasePath;
  unreadMessageCount: number = 0;
  expiringKafalaCount: number = 0;
  isChatPage: boolean = false;
  isKafalaPage: boolean = false;
  isAdminOrSuperAdmin: boolean = false; // New property to check admin roles
  private unreadCountSubscription: Subscription | undefined;
  private expiringKafalaSubscription: Subscription | undefined;
  private routerSubscription: Subscription | undefined;
  clubs: string = 'clubs';
  isUserRole: boolean = false;
  isEtabSelected: boolean = false;
  profilePictureUrl: string | null = null;
  etabLogoUrl: string | null = null;
  private selectedEtabSubscription: Subscription | undefined;
  private profilePictureSubscription: Subscription | undefined;

  constructor(
    private router: Router,
    public languageService: LanguageService,
    private userService: UserService,
    private etablissementService: EtablissementService,
    private selectedEtablissementService: SelectedEtablissementService,
    private chatNotificationService: ChatNotificationService,
    private inscritService: InscritService,
    private webSocketService: WebSocketService,
    private authService: AuthenticationService
  ) {}

  ngOnInit() {
    this.isLoginPage = this.router.url === '/login';
    this.checkUserRole();
    this.loadProfilePicture();

    // Subscribe to unread message count
    this.unreadCountSubscription = this.chatNotificationService.getUnreadCount().subscribe(count => {
      this.unreadMessageCount = count;
    });

    // Subscribe to router events to detect chat and kafala pages
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isChatPage = event.urlAfterRedirects.includes('/chat');
        this.isKafalaPage = event.urlAfterRedirects.includes('/kafala-expiration');
      });

    // Check initial route
    this.isChatPage = this.router.url.includes('/chat');
    this.isKafalaPage = this.router.url.includes('/kafala-expiration');

    // Handle enterprise logo and establishment selection
    this.selectedEtabSubscription = this.selectedEtablissementService.selectedEtabId$.subscribe(etabId => {
      this.isEtabSelected = etabId !== null;
      if (etabId) {
        this.loadEtablissementLogo(etabId);
        this.loadExpiringKafalaCount(etabId);
      } else {
        this.etabLogoUrl = null;
        this.expiringKafalaCount = 0;
      }
    });

    const initialEtabId = this.selectedEtablissementService.getSelectedEtablissement();
    this.isEtabSelected = initialEtabId !== null;
    if (initialEtabId) {
      this.loadEtablissementLogo(initialEtabId);
      this.loadExpiringKafalaCount(initialEtabId);
    }

    // Connexion WebSocket pour recevoir les notifications en temps réel
    // même quand l'utilisateur n'est pas sur la page chat
    if (this.authService.isUserLoggedIn() && !this.isChatPage) {
      this.initializeWebSocketForNotifications();
    }
  }

  private async initializeWebSocketForNotifications(): Promise<void> {
    const currentUserId = this.authService.getUserId();
    if (!currentUserId) return;

    try {
      // Connecter au WebSocket si pas déjà connecté
      this.webSocketService.connect();

      // S'abonner aux messages pour cet utilisateur
      const messageSubscription = (await this.webSocketService.subscribeToUser(currentUserId))
        .subscribe((message) => {
          if (message && message.sender?.id !== currentUserId && !this.isChatPage) {
            // L'utilisateur a reçu un nouveau message et n'est PAS sur la page chat
            // Mettre à jour le compteur dans le navbar
            this.unreadMessageCount++;
            this.chatNotificationService.setUnreadCount(this.unreadMessageCount);
          }
        });

      if (this.unreadCountSubscription) {
        this.unreadCountSubscription.add(messageSubscription);
      }
    } catch (error) {
      console.error('Failed to initialize WebSocket for notifications:', error);
    }
  }

  ngOnDestroy() {
    if (this.selectedEtabSubscription) {
      this.selectedEtabSubscription.unsubscribe();
    }
    if (this.profilePictureSubscription) {
      this.profilePictureSubscription.unsubscribe();
    }
    if (this.unreadCountSubscription) {
      this.unreadCountSubscription.unsubscribe();
    }
    if (this.expiringKafalaSubscription) {
      this.expiringKafalaSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  toggleSidebar() {
    this.sidebarToggled.emit(!this.isSidebarOpen);
  }

  toggleLanguage() {
    this.languageService.toggleLanguage();
  }

  logOut() {
    sessionStorage.clear();
    this.selectedEtablissementService.setSelectedEtablissement(null);
    this.chatNotificationService.setUnreadCount(0);
    this.expiringKafalaCount = 0;
    this.router.navigate(['/login']);
  }

  getDirection(): string {
    return this.languageService.getCurrentLang() === 'ar' ? 'rtl' : 'ltr';
  }

  checkUserRole(): void {
    const authUser = JSON.parse(sessionStorage.getItem('authUser') || '{}');
    const userRoles: Role[] = authUser?.roles || [];
    this.isUserRole = userRoles.length === 1 && userRoles.some(role => role.roleName === 'USER');
    // Check if user is ADMIN or SUPER_ADMIN
    this.isAdminOrSuperAdmin = userRoles.some(role => role.roleName === 'ADMIN' || role.roleName === 'SUPER_ADMIN');
  }

  loadProfilePicture(): void {
    const authUser = JSON.parse(sessionStorage.getItem('authUser') || '{}');
    const userId = authUser?.id;
    if (userId) {
      this.profilePictureSubscription = this.userService.getProfilePictureObservable().subscribe(cache => {
        const imageUrl = cache[userId];
        if (imageUrl) {
          this.profilePictureUrl = imageUrl.startsWith('/profile-pictures/')
            ? `${this.basePath}${imageUrl}`
            : imageUrl || null;
        } else {
          this.profilePictureUrl = null;
          this.userService.getProfilePicture(userId).subscribe();
        }
      });
    }
  }

  // Called when the profile image element triggers an error (e.g., 404)
  onProfileImageError(): void {
    this.profilePictureUrl = null;
  }

  loadEtablissementLogo(etabId: number): void {
    this.etablissementService.getEtablissementLogo(etabId).subscribe({
      next: (logoUrl: string) => {
        this.etabLogoUrl = logoUrl && logoUrl.startsWith('/uploads/logos/')
          ? `${this.basePath}${logoUrl}`
          : logoUrl || null;
      },
      error: (err) => {
        console.error('Error loading establishment logo:', err);
        this.etabLogoUrl = null;
      }
    });
  }

  // Called when the establishment logo image element triggers an error
  onEtabLogoError(): void {
    this.etabLogoUrl = null;
  }

  openChat(): void {
    this.router.navigate(['/chat']);
  }

  openKafalaExpirations(): void {
    this.router.navigate(['/kafala-expiration']);
  }

  private loadExpiringKafalaCount(etabId: number): void {
    this.expiringKafalaSubscription = this.inscritService.getExpiringAndExpiredKafalaCount(etabId).subscribe({
      next: (count) => {
        this.expiringKafalaCount = count;
      },
      error: (err) => {
        console.error('Error loading expiring kafala count:', err);
        this.expiringKafalaCount = 0;
      }
    });
  }
}
